import { Component } from 'react'

// Catches render errors anywhere below it and shows a visible, copyable
// error card instead of leaving the screen blank. Without this, React 18
// unmounts the whole tree on any uncaught render error — which on iOS
// PWAs surfaces as a "blank screen" with nothing to debug.
//
// Surfaces:
//   * The error message (so the user can read or screenshot it)
//   * The component stack (only when present; React only fills this in
//     during the error, not on every render)
//   * A "Reload app" button that hard-reloads, bypassing the service-worker
//     cache where possible.
//   * A "Sign out" escape hatch for cases where corrupt session/profile
//     data is the trigger — clears Supabase session tokens + the active
//     round cache, then reloads.
//
// Optional `fallback` prop: when provided, the boundary renders it INSTEAD of
// the full-screen card. Pass a function `({ error, reset }) => ReactNode` (or a
// static node) to contain a failure to a small subtree — e.g. the GPS-map lazy
// import on the scorecard, so a purged map chunk degrades to "map unavailable"
// rather than replacing the whole scorecard mid-round. Omit it (the root usage)
// to keep the full-screen behavior.

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, info: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    this.setState({ info })
    try { console.error('[ErrorBoundary]', error, info) } catch {}
  }

  reset = () => {
    this.setState({ error: null, info: null })
  }

  reload = () => {
    try {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(regs => {
          regs.forEach(r => r.unregister())
        }).catch(() => {})
      }
    } catch {}
    window.location.reload()
  }

  hardReset = () => {
    try {
      // Clear known per-device caches that can corrupt the auth/round state.
      const keep = []
      const drop = []
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        if (k && (k.startsWith('sb-') || k.startsWith('tf') || k.startsWith('active_round') || k.startsWith('standalone_round_'))) {
          drop.push(k)
        } else if (k) {
          keep.push(k)
        }
      }
      drop.forEach(k => { try { localStorage.removeItem(k) } catch {} })
    } catch {}
    this.reload()
  }

  render() {
    if (!this.state.error) return this.props.children

    // Scoped fallback (e.g. the GPS-map subtree) — render it instead of the
    // full-screen card so a contained failure never takes over the whole page.
    if (this.props.fallback != null) {
      return typeof this.props.fallback === 'function'
        ? this.props.fallback({ error: this.state.error, reset: this.reset })
        : this.props.fallback
    }

    const msg = String(this.state.error?.message || this.state.error || 'Unknown error')
    const stack = String(this.state.error?.stack || '')
    const compStack = String(this.state.info?.componentStack || '')

    return (
      <div style={{
        minHeight: '100vh',
        padding: '32px 20px',
        background: '#f4f1ea',
        color: '#1b2a1f',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        WebkitOverflowScrolling: 'touch',
      }}>
        <div style={{
          maxWidth: 520,
          margin: '0 auto',
          background: 'white',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 4px 18px rgba(0,0,0,0.08)',
        }}>
          <h1 style={{ marginTop: 0, fontSize: '1.4rem', color: '#b23b2e' }}>Something went wrong</h1>
          <p style={{ fontSize: '0.95rem', marginBottom: 18 }}>
            The app hit an unexpected error and stopped rendering. Tap Reload below to try again. If it keeps happening, take a screenshot of the message below and send it over.
          </p>

          <div style={{
            background: '#f9f6ee',
            border: '1px solid #e6e0d4',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.82rem',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            maxHeight: '40vh',
            overflow: 'auto',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{msg}</div>
            {stack && <div style={{ opacity: 0.75 }}>{stack.split('\n').slice(0, 6).join('\n')}</div>}
            {compStack && <div style={{ opacity: 0.55, marginTop: 8 }}>{compStack.split('\n').slice(0, 5).join('\n')}</div>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={this.reload}
              style={{ padding: '12px 16px', border: 'none', borderRadius: 10, background: '#1f6b3a', color: 'white', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}
            >
              Reload app
            </button>
            <button
              onClick={this.hardReset}
              style={{ padding: '12px 16px', border: '1px solid #d8d2c4', borderRadius: 10, background: 'white', color: '#1b2a1f', fontSize: '0.95rem', cursor: 'pointer' }}
            >
              Reset session and reload
            </button>
          </div>
        </div>
      </div>
    )
  }
}
